import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Mail, LayoutTemplate as TplIcon } from "lucide-react";

const CATEGORY_LABEL = {
  transactional: { label: "Transaccional", bg: "bg-primary/10", text: "text-primary", border: "border-primary/30" },
  marketing: { label: "Marketing", bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30" },
  drip: { label: "Drip", bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30" },
};

export default function EmailTemplates() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = category ? `?category=${category}` : "";
      const r = await api.get(`/admin/email-templates${q}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [category]);

  const createNew = async () => {
    const name = prompt("Nombre de la nueva plantilla:");
    if (!name) return;
    try {
      const r = await api.post("/admin/email-templates", {
        name,
        subject_template: "Nuevo email · {{first_name}}",
        preheader: "",
        category: "marketing",
        blocks: [],
      });
      const base = superadmin ? "/app/superadmin" : "/app/staff";
      window.location.href = `${base}/email/templates/${r.data.id}`;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/admin/email-templates/${id}/duplicate`);
      toast.success("Plantilla duplicada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!confirm(`¿Eliminar plantilla "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/email-templates/${t.id}`);
      toast.success("Plantilla eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const base = superadmin ? "/app/superadmin" : "/app/staff";

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">
              Fase 13.5 · Email marketing
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">
              Plantillas de email
            </h1>
            <p className="mt-1 text-sm text-gray-400">
              Diseña emails con bloques reutilizables. Se usan como base para campañas.
            </p>
          </div>
          <button
            onClick={createNew}
            data-testid="template-new-btn"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-primary hover:brightness-110 text-primary-foreground text-sm font-semibold shadow-sm"
          >
            <Plus className="w-4 h-4" /> Nueva plantilla
          </button>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { v: "", l: "Todas" },
            { v: "transactional", l: "Transaccionales" },
            { v: "marketing", l: "Marketing" },
            { v: "drip", l: "Drip" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setCategory(f.v)}
              className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest border ${
                category === f.v
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-gray-800 text-gray-400 hover:bg-white/5"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-gray-400 py-12">Cargando…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.length === 0 && (
              <div className="col-span-full p-6 rounded-xl border border-gray-800 text-center text-gray-500">
                Sin plantillas. Crea una nueva.
              </div>
            )}
            {items.map((t) => {
              const cat = CATEGORY_LABEL[t.category] || CATEGORY_LABEL.marketing;
              const heroBlock = (t.blocks || []).find((b) => b.type === "email_hero");
              const heroTitle = heroBlock?.props?.title || t.subject_template || t.name;
              return (
                <div
                  key={t.id}
                  className="rounded-xl border border-gray-800 bg-[#111827] overflow-hidden hover:border-primary/50 transition-colors"
                  data-testid={`template-card-${t.slug}`}
                >
                  <div className="h-32 bg-[#0B0F14] p-4 flex items-center justify-center relative">
                    <div className="text-center">
                      <TplIcon className="w-8 h-8 text-primary/40 mx-auto mb-2" />
                      <p className="text-xs text-gray-400 line-clamp-2">{heroTitle}</p>
                    </div>
                    <span
                      className={`absolute top-2 right-2 text-[9px] px-2 py-0.5 rounded uppercase font-bold tracking-widest border ${cat.bg} ${cat.text} ${cat.border}`}
                    >
                      {cat.label}
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-bold text-white truncate">{t.name}</p>
                    <p className="text-xs text-gray-500 font-mono mt-0.5 truncate">{t.slug}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500 uppercase tracking-widest font-semibold">
                      <span>{t.blocks?.length || 0} bloques</span>
                      <span>·</span>
                      <span>{t.tenant_scope}</span>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Link
                        to={`${base}/email/templates/${t.id}`}
                        data-testid={`template-open-${t.slug}`}
                        className="flex-1 text-center px-3 py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold border border-primary/30"
                      >
                        Editar
                      </Link>
                      <button
                        onClick={() => duplicate(t.id)}
                        data-testid={`template-duplicate-${t.slug}`}
                        aria-label="Duplicar"
                        className="p-1.5 rounded-md border border-gray-800 text-gray-400 hover:text-white hover:bg-white/5"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(t)}
                        data-testid={`template-delete-${t.slug}`}
                        aria-label="Eliminar"
                        className="p-1.5 rounded-md border border-gray-800 text-gray-400 hover:text-destructive hover:bg-white/5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </StaffLayout>
  );
}
